import classNames from "classnames";
import "./Label.css";

export type Props = {
    className?: string;
    fontStyle?: "italic";
    transform?: "uppercase";
    disabled?: boolean;
    children: React.ReactNode;
    nowrap?: boolean;
};

type BaseLabel = (className?: string) => (props: Props) => React.ReactNode;

function BaseLabel(baseClassName?: string) {
    return function Label(props: Props) {
        const { className, children, fontStyle, transform, disabled, nowrap } = props;
        return (
            <span
                className={classNames(
                    "web-lib__label",
                    baseClassName,
                    {
                        "web-lib__label--disabled": disabled,
                        "web-lib__label--italic": fontStyle === "italic",
                        "web-lib__label--uppercase": transform === "uppercase",
                        "web-lib__label--nowrap": nowrap,
                    },
                    className,
                )}
            >
                {children}
            </span>
        );
    };
}

function Add(baseLabel: BaseLabel, className: string) {
    return (newClassName?: string) => baseLabel(classNames(className, newClassName));
}

function Curry(baseLabel: BaseLabel) {
    return (className?: string) => baseLabel(className);
}

const Colors = (baseLabel: BaseLabel) =>
    Object.assign(baseLabel("text-text-primary"), {
        Text1: baseLabel("text-text-primary"),
        Text1Inverse: baseLabel("text-text-inverse-primary"),
        Text2: baseLabel("text-text-secondary"),
        Text2Inverse: baseLabel("text-text-inverse-secondary"),
        Text3: baseLabel("text-text-tertiary"),
        Highlight: baseLabel("text-text-highlight"),
        Error: baseLabel("text-status-error"),
        Warning: baseLabel("text-status-warning"),
        Success: baseLabel("text-status-success"),
        Inactive: baseLabel("text-status-inactive"),
        NavigationSelected: baseLabel("text-text-navigation-selected"),
        NavigationUnselected: baseLabel("text-text-navigation-unselected"),
    });

const Weights = (baseComponent: BaseLabel) =>
    Object.assign(baseComponent(), {
        Reqular: Colors(Add(baseComponent, "web-lib__label--reqular")),
        MediumBold: Colors(Add(baseComponent, "web-lib__label--medium-bold")),
        Bold: Colors(Add(baseComponent, "web-lib__label--bold")),
        XBold: Colors(Add(baseComponent, "web-lib__label--xbold")),

        ...Colors(Curry(baseComponent)),
    });

const Sizes = (baseComponent: BaseLabel) =>
    Object.assign(baseComponent(), {
        Large: Weights(Add(baseComponent, "web-lib__label--large")),
        Medium: Weights(Add(baseComponent, "web-lib__label--medium")),
        Small: Weights(Add(baseComponent, "web-lib__label--small")),
        XSmall: Weights(Add(baseComponent, "web-lib__label--xsmall")),
    });

const Label = Sizes(BaseLabel);

export default Label;
